import { create } from 'zustand';
import { useEditorStore } from './editorStore';
const normalizeDimension = (value) => typeof value === 'number' && value > 0 ? value : 500;
export const useMovieStore = create((set) => ({
    // Initial state
    userAllMovies: [],
    userRecentMovies: [],
    sampleMovies: [],
    selectedMovieId: null,
    currentMovieResponse: null,
    currentMovieSummary: null,
    currentMovie: null,
    currentScene: null,
    currentLayer: null,
    library: [],
    clipFolders: [],
    isLoading: false,
    error: null,
    setSelectedMovieId: (id) => set({ selectedMovieId: id }),
    setCurrentMovieResponse: (movie) => {
        const normalizedMovie = movie
            ? {
                ...movie,
                width: normalizeDimension(movie.width),
                height: normalizeDimension(movie.height),
            }
            : null;
        set({ currentMovieResponse: normalizedMovie });
        // Update currentMovieSummary with calculated values
        if (normalizedMovie) {
            // Calculate total frames from all scenes
            const totalFrames = normalizedMovie.scenes.reduce((sum, scene) => sum + scene.framesTotal, 0);
            // Calculate duration: totalFrames / fps (in seconds)
            const duration = normalizedMovie.fps > 0 ? totalFrames / normalizedMovie.fps : 0;
            // Format resolution: width x height
            const resolution = `${normalizedMovie.width}x${normalizedMovie.height}`;
            // Create/update MovieSummary
            const summary = {
                id: normalizedMovie.id,
                movieId: normalizedMovie.id,
                name: normalizedMovie.name,
                width: normalizedMovie.width,
                height: normalizedMovie.height,
                fps: normalizedMovie.fps,
                duration: duration,
                resolution: resolution,
            };
            set({ currentMovieSummary: summary });
        }
        else {
            set({ currentMovieSummary: null });
        }
    },
    deleteMovie: (movieId) => set((state) => ({
        userAllMovies: state.userAllMovies.filter((movie) => movie.id !== movieId),
        userRecentMovies: state.userRecentMovies.filter((movie) => (movie.movieId ?? movie.id) !== movieId),
    })),
    unloadMovie: () => {
        useEditorStore.getState().resetAiChat();
        set({
            selectedMovieId: null,
            currentMovie: null,
            currentMovieSummary: null,
            currentMovieResponse: null,
            currentScene: null,
            currentLayer: null,
            library: [],
            clipFolders: [],
        });
    },
    setSampleMovies: (movies) => set({ sampleMovies: movies }),
    setUserAllMovies: (movies) => set({ userAllMovies: movies }),
    setUserRecentMovies: (movies) => set({ userRecentMovies: movies }),
    setCurrentMovieSummary: (summary) => set({ currentMovieSummary: summary }),
    // Movie actions
    setMovie: (movie) => {
        const normalizedMovie = movie
            ? {
                ...movie,
                width: normalizeDimension(movie.width),
                height: normalizeDimension(movie.height),
            }
            : null;
        set({ currentMovie: normalizedMovie });
        if (normalizedMovie && normalizedMovie.scenes.length > 0) {
            set({ currentScene: normalizedMovie.scenes[0] });
        }
    },
    updateMovie: (updates) => set((state) => {
        const updatedMovie = state.currentMovie
            ? {
                ...state.currentMovie,
                ...updates,
                width: normalizeDimension(updates.width ?? state.currentMovie.width),
                height: normalizeDimension(updates.height ?? state.currentMovie.height),
            }
            : null;
        const movieId = updatedMovie?.id ?? state.currentMovie?.id;
        // Helper to update a movie summary in a list
        const updateSummaryInList = (list) => list.map((item) => {
            const itemId = item.movieId ?? item.id;
            if (String(itemId) === String(movieId)) {
                return {
                    ...item,
                    name: updatedMovie?.name ?? item.name,
                    description: updatedMovie?.description ?? item.description,
                    width: updatedMovie?.width ?? item.width,
                    height: updatedMovie?.height ?? item.height,
                    fps: updatedMovie?.fps ?? item.fps,
                };
            }
            return item;
        });
        // If currentMovieResponse exists, update it and recalculate summary
        let updatedResponse = state.currentMovieResponse;
        if (updatedResponse && updatedMovie && String(updatedResponse.id) === String(movieId)) {
            updatedResponse = {
                ...updatedResponse,
                name: updatedMovie.name,
                width: updatedMovie.width,
                height: updatedMovie.height,
                fps: updatedMovie.fps,
            };
            // Recalculate summary
            const totalFrames = updatedResponse.scenes.reduce((sum, scene) => sum + scene.framesTotal, 0);
            const duration = updatedResponse.fps > 0 ? totalFrames / updatedResponse.fps : 0;
            const resolution = `${updatedResponse.width}x${updatedResponse.height}`;
            const summary = {
                id: updatedResponse.id,
                movieId: updatedResponse.id,
                name: updatedResponse.name,
                width: updatedResponse.width,
                height: updatedResponse.height,
                fps: updatedResponse.fps,
                duration: duration,
                resolution: resolution,
            };
            return {
                currentMovie: updatedMovie,
                currentMovieResponse: updatedResponse,
                currentMovieSummary: summary,
                userAllMovies: updateSummaryInList(state.userAllMovies),
                userRecentMovies: updateSummaryInList(state.userRecentMovies),
            };
        }
        return {
            currentMovie: updatedMovie,
            userAllMovies: updateSummaryInList(state.userAllMovies),
            userRecentMovies: updateSummaryInList(state.userRecentMovies),
        };
    }),
    // Layer actions
    setCurrentLayer: (layer) => set({ currentLayer: layer }),
    // Scene actions
    setCurrentScene: (scene) => set({ currentScene: scene, currentLayer: null }),
    setCurrentSceneById: (sceneId) => set((state) => ({
        currentScene: state.currentMovie?.scenes.find((s) => s.id === sceneId) ?? state.currentScene,
        currentLayer: null,
    })),
    addScene: (scene) => set((state) => ({
        currentMovie: state.currentMovie
            ? { ...state.currentMovie, scenes: [...state.currentMovie.scenes, scene] }
            : null,
    })),
    updateScene: (sceneId, updates) => set((state) => ({
        currentMovie: state.currentMovie
            ? {
                ...state.currentMovie,
                scenes: state.currentMovie.scenes.map((s) => s.id === sceneId ? { ...s, ...updates } : s),
            }
            : null,
        currentScene: state.currentScene?.id === sceneId
            ? { ...state.currentScene, ...updates }
            : state.currentScene,
    })),
    deleteScene: (sceneId) => set((state) => {
        if (!state.currentMovie)
            return state;
        const currentScenes = state.currentMovie.scenes;
        const deletedIndex = currentScenes.findIndex((s) => s.id === sceneId);
        const newScenes = state.currentMovie.scenes.filter((s) => s.id !== sceneId);
        let newCurrentScene = state.currentScene;
        if (state.currentScene?.id === sceneId) {
            if (deletedIndex > 0) {
                newCurrentScene = newScenes[deletedIndex - 1] ?? newScenes[0] ?? null;
            }
            else {
                newCurrentScene = newScenes[0] ?? null;
            }
        }
        return {
            currentMovie: { ...state.currentMovie, scenes: newScenes },
            currentScene: newCurrentScene,
        };
    }),
    reorderScenes: (sceneIds) => set((state) => {
        if (!state.currentMovie)
            return state;
        const sceneMap = new Map(state.currentMovie.scenes.map((s) => [s.id, s]));
        const reordered = sceneIds
            .map((id, index) => {
            const scene = sceneMap.get(id);
            return scene ? { ...scene, order: index } : null;
        })
            .filter((s) => s !== null);
        return { currentMovie: { ...state.currentMovie, scenes: reordered } };
    }),
    // Scene object actions
    addSceneObject: (object) => set((state) => {
        if (!state.currentScene)
            return state;
        const currentObjects = state.currentScene.objects ?? [];
        const withoutExisting = currentObjects.filter((item) => item.id !== object.id);
        const updatedScene = {
            ...state.currentScene,
            objects: [...withoutExisting, object],
        };
        return {
            currentScene: updatedScene,
            currentMovie: state.currentMovie
                ? {
                    ...state.currentMovie,
                    scenes: state.currentMovie.scenes.map((s) => s.id === state.currentScene.id ? updatedScene : s),
                }
                : null,
        };
    }),
    updateSceneObject: (objectId, updates) => set((state) => {
        if (!state.currentScene)
            return state;
        const currentObjects = state.currentScene.objects ?? [];
        const updatedObjects = currentObjects.map((obj) => obj.id === objectId ? { ...obj, ...updates } : obj);
        const updatedScene = { ...state.currentScene, objects: updatedObjects };
        return {
            currentScene: updatedScene,
            currentMovie: state.currentMovie
                ? {
                    ...state.currentMovie,
                    scenes: state.currentMovie.scenes.map((s) => s.id === state.currentScene.id ? updatedScene : s),
                }
                : null,
        };
    }),
    batchUpdateSceneObjects: (updatesById) => set((state) => {
        if (!state.currentScene)
            return state;
        const ids = Object.keys(updatesById);
        if (ids.length === 0)
            return state;
        const idSet = new Set(ids);
        const currentObjects = state.currentScene.objects ?? [];
        const updatedObjects = currentObjects.map((obj) => idSet.has(obj.id) ? { ...obj, ...updatesById[obj.id] } : obj);
        const updatedScene = { ...state.currentScene, objects: updatedObjects };
        return {
            currentScene: updatedScene,
            currentMovie: state.currentMovie
                ? {
                    ...state.currentMovie,
                    scenes: state.currentMovie.scenes.map((s) => s.id === state.currentScene.id ? updatedScene : s),
                }
                : null,
        };
    }),
    deleteSceneObject: (objectId) => set((state) => {
        if (!state.currentScene)
            return state;
        const currentObjects = state.currentScene.objects ?? [];
        const updatedScene = {
            ...state.currentScene,
            objects: currentObjects.filter((obj) => obj.id !== objectId),
        };
        return {
            currentScene: updatedScene,
            currentMovie: state.currentMovie
                ? {
                    ...state.currentMovie,
                    scenes: state.currentMovie.scenes.map((s) => s.id === state.currentScene.id ? updatedScene : s),
                }
                : null,
        };
    }),
    reorderSceneObjects: (objectIds) => set((state) => {
        if (!state.currentScene)
            return state;
        const objectMap = new Map((state.currentScene.objects ?? []).map((o) => [o.id, o]));
        const reordered = objectIds
            .map((id, index) => {
            const obj = objectMap.get(id);
            return obj ? { ...obj, zIndex: index } : null;
        })
            .filter((o) => o !== null);
        const updatedScene = { ...state.currentScene, objects: reordered };
        return {
            currentScene: updatedScene,
            currentMovie: state.currentMovie
                ? {
                    ...state.currentMovie,
                    scenes: state.currentMovie.scenes.map((s) => s.id === state.currentScene.id ? updatedScene : s),
                }
                : null,
        };
    }),
    // Library actions
    setLibrary: (items) => set({ library: items }),
    addLibraryItem: (item) => set((state) => ({ library: [...state.library, item] })),
    deleteLibraryItem: (itemId) => set((state) => ({
        library: state.library.filter((i) => i.id !== itemId),
    })),
    // Clip folder actions
    setClipFolders: (folders) => set({ clipFolders: folders }),
    addClipFolder: (folder) => set((state) => ({ clipFolders: [...state.clipFolders, folder] })),
    deleteClipFolder: (folderId) => set((state) => ({
        clipFolders: state.clipFolders.filter((f) => f.id !== folderId),
    })),
    // Loading state
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
}));
